import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Layers } from "lucide-react";
import { decryptToken } from "../utils/getToken";
import { getSessionMainId } from "../utils/getCookie";
import AdmissionCenterSelect from "./AdmissionCenterSelect";
import "./AddAdmission.css";

const WEEKDAYS = [
    { v: "Mon", l: "Mon" },
    { v: "Tue", l: "Tue" },
    { v: "Wed", l: "Wed" },
    { v: "Thu", l: "Thu" },
    { v: "Fri", l: "Fri" },
    { v: "Sat", l: "Sat" },
    { v: "Sun", l: "Sun" },
];

export default function AdmissionBatchEditor() {
    const { batchId } = useParams();
    const isEdit = batchId != null && batchId !== "new";
    const id = isEdit ? batchId : null;
    const navigate = useNavigate();
    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const [staff, setStaff] = useState([]);
    const [staffLoadError, setStaffLoadError] = useState("");
    const [load, setLoad] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: "",
        center_id: "",
        teacher_id: "",
        start_time: "",
        end_time: "",
        days: [],
        start_date: "",
        end_date: "",
        max_students: "",
        status: "Active",
        batch_type: "",
        notes: "",
    });

    useEffect(() => {
        let cancelled = false;
        setStaffLoadError("");
        (async () => {
            try {
                const mainId = getSessionMainId();
                const qs = mainId ? `?main_id=${encodeURIComponent(mainId)}` : "";
                const res = await fetch(`${url}/staff${qs}`, { headers });
                const data = await res.json().catch(() => null);
                if (cancelled) return;
                if (!res.ok) {
                    const msg =
                        (data && (data.message || data.error)) ||
                        `Could not load teachers (${res.status})`;
                    setStaff([]);
                    setStaffLoadError(msg);
                    return;
                }
                if (Array.isArray(data)) {
                    setStaff(data);
                } else {
                    setStaff([]);
                    setStaffLoadError("Unexpected response from staff API.");
                }
            } catch (e) {
                if (!cancelled) {
                    setStaff([]);
                    setStaffLoadError(e?.message || "Failed to load team directory.");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, url]);

    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        (async () => {
            setLoad(true);
            try {
                const res = await fetch(`${url}/admission/batches/${id}`, { headers });
                const j = await res.json();
                if (!res.ok || cancelled) return;
                const b = j.batch;
                if (!b) return;
                setForm({
                    name: b.name || "",
                    center_id: b.center_id || "",
                    teacher_id: b.teacher_id != null ? String(b.teacher_id) : "",
                    start_time: b.start_time || "",
                    end_time: b.end_time || "",
                    days: Array.isArray(b.days) ? b.days : [],
                    start_date: b.start_date || "",
                    end_date: b.end_date || "",
                    max_students: b.max_students != null ? String(b.max_students) : "",
                    status: b.status === "Inactive" ? "Inactive" : "Active",
                    batch_type: b.batch_type || "",
                    notes: b.notes || "",
                });
            } catch {
                /* ignore */
            } finally {
                if (!cancelled) setLoad(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isEdit, id, url, token]);

    const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

    const toggleDay = (v) => {
        setForm((p) => {
            const has = p.days.includes(v);
            return {
                ...p,
                days: has ? p.days.filter((d) => d !== v) : [...p.days, v].sort(
                    (a, b) => WEEKDAYS.findIndex((x) => x.v === a) - WEEKDAYS.findIndex((x) => x.v === b)
                ),
            };
        });
    };

    const handleCenterSelect = (e) => {
        setField("center_id", e.target.value);
    };

    const networkErrorHint =
        "Could not reach the API. Check that the server is running and deployed, try http://localhost (not 127.0.0.1) vs your CORS allowlist, disable extensions that block requests, or use a private/incognito window.";

    /**
     * Must not be used as `async onSubmit` — React does not await it, so a rejected
     * Promise surfaces as an unhandled rejection and CRA shows a full-screen error.
     */
    const saveBatch = async () => {
        setSaving(true);
        try {
            const body = {
                name: form.name.trim(),
                center_id: form.center_id.trim(),
                teacher_id: form.teacher_id || null,
                start_time: form.start_time.trim(),
                end_time: form.end_time.trim(),
                days: form.days,
                start_date: form.start_date || null,
                end_date: form.end_date || null,
                max_students: form.max_students === "" ? null : parseInt(form.max_students, 10),
                status: form.status,
                batch_type: form.batch_type || null,
                notes: form.notes || null,
            };
            const apiUrl = isEdit ? `${url}/admission/batches/${id}` : `${url}/admission/batches`;
            const res = await fetch(apiUrl, {
                method: isEdit ? "PUT" : "POST",
                headers,
                body: JSON.stringify(body),
            });
            const text = await res.text();
            let j = {};
            try {
                j = text ? JSON.parse(text) : {};
            } catch {
                j = { message: text?.slice(0, 200) || "Invalid response" };
            }
            if (!res.ok) {
                alert(j.message || j.error || `Save failed (${res.status})`);
                return;
            }
            alert(isEdit ? "Batch updated" : "Batch created");
            navigate("/admission-batches");
        } catch (err) {
            const msg = String(err?.message || err || "");
            const isFetchFailed =
                err?.name === "TypeError" && (msg.includes("fetch") || msg.includes("Failed to fetch"));
            alert(isFetchFailed ? networkErrorHint : msg || "Error");
        } finally {
            setSaving(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        void saveBatch().catch((err) => {
            console.error("Batch save:", err);
            setSaving(false);
        });
    };

    if (load) {
        return (
            <div className="admission-form-shell">
                <div className="admission-form-container">
                    <p style={{ padding: 24 }}>Loading…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admission-form-shell">
            <div className="admission-form-shell__bg" aria-hidden />
            <div className="admission-form-container">
                <header className="admission-form-header">
                    <button
                        type="button"
                        className="admission-form-back"
                        onClick={() => navigate("/admission-batches")}
                    >
                        <ArrowLeft size={18} strokeWidth={2.25} aria-hidden />
                        Batches
                    </button>
                    <div className="admission-form-header-text">
                        <h1>
                            <Layers size={22} style={{ display: "inline", verticalAlign: "middle", marginRight: 8 }} aria-hidden />
                            {isEdit ? "Edit batch" : "New batch"}
                        </h1>
                        <p>Basic info, schedule (required for attendance), capacity, and optional type/notes.</p>
                    </div>
                </header>

                <div className="admission-form-card">
                    <form className="admission-form-premium" onSubmit={onSubmit}>
                        <section className="admission-form-section" aria-labelledby="ab-basic">
                            <h2 id="ab-basic" className="admission-form-section__title">
                                Basic info
                            </h2>
                            <div className="admission-form-grid">
                                <div className="admission-form-field admission-form-field--full">
                                    <label className="admission-form-field__label" htmlFor="ab-name">
                                        Batch name<span className="req">*</span>
                                    </label>
                                    <input
                                        id="ab-name"
                                        className="admission-form-input"
                                        value={form.name}
                                        onChange={(e) => setField("name", e.target.value)}
                                        placeholder="e.g. Morning Batch, Batch A"
                                        required
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="ab-center">
                                        Center<span className="req">*</span>
                                    </label>
                                    <AdmissionCenterSelect
                                        name="center_id"
                                        value={form.center_id}
                                        onChange={handleCenterSelect}
                                        token={token}
                                        className="admission-form-select"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="ab-teacher">
                                        Teacher
                                    </label>
                                    <select
                                        id="ab-teacher"
                                        className="admission-form-select"
                                        value={form.teacher_id}
                                        onChange={(e) => setField("teacher_id", e.target.value)}
                                    >
                                        <option value="">Select (team directory)</option>
                                        {staff
                                            .filter(
                                                (s) =>
                                                    s.emplyee_ID != null &&
                                                    String(s.emplyee_ID).trim() !== ""
                                            )
                                            .map((s) => (
                                                <option
                                                    key={String(s.id) + "-" + String(s.emplyee_ID)}
                                                    value={String(s.emplyee_ID).trim()}
                                                >
                                                    {[s.firstName, s.lastName].filter(Boolean).join(" ")}{" "}
                                                    ({s.emplyee_ID})
                                                </option>
                                            ))}
                                    </select>
                                    {staffLoadError && (
                                        <p className="admission-file-upload__hint" style={{ color: "#b91c1c" }}>
                                            {staffLoadError}
                                        </p>
                                    )}
                                    {!staffLoadError &&
                                        staff.length > 0 &&
                                        staff.every(
                                            (s) =>
                                                s.emplyee_ID == null ||
                                                String(s.emplyee_ID).trim() === ""
                                        ) && (
                                            <p className="admission-file-upload__hint">
                                                Staff exist but none have an employee ID. Set{" "}
                                                <strong>emplyee_ID</strong> in Team Directory for each teacher.
                                            </p>
                                        )}
                                    {!staffLoadError && staff.length === 0 && (
                                        <p className="admission-file-upload__hint">
                                            No staff for this account yet. Add people under{" "}
                                            <strong>Teams → Team Directory</strong>.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </section>

                        <section className="admission-form-section" aria-labelledby="ab-schedule">
                            <h2 id="ab-schedule" className="admission-form-section__title">
                                Schedule details
                            </h2>
                            <p className="admission-file-upload__hint" style={{ marginBottom: 12 }}>
                                Without days and time windows, batch attendance logic cannot run reliably.
                            </p>
                            <div className="admission-form-grid">
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="ab-start-t">
                                        Start time<span className="req">*</span>
                                    </label>
                                    <input
                                        id="ab-start-t"
                                        type="time"
                                        className="admission-form-input"
                                        value={form.start_time}
                                        onChange={(e) => setField("start_time", e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="ab-end-t">
                                        End time<span className="req">*</span>
                                    </label>
                                    <input
                                        id="ab-end-t"
                                        type="time"
                                        className="admission-form-input"
                                        value={form.end_time}
                                        onChange={(e) => setField("end_time", e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="admission-form-field admission-form-field--full">
                                    <span className="admission-form-field__label">
                                        Days of operation<span className="req">*</span>
                                    </span>
                                    <div className="admission-batch-days" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                        {WEEKDAYS.map((d) => (
                                            <label key={d.v} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.days.includes(d.v)}
                                                    onChange={() => toggleDay(d.v)}
                                                />
                                                {d.l}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="ab-start-d">
                                        Start date
                                    </label>
                                    <input
                                        id="ab-start-d"
                                        type="date"
                                        className="admission-form-input"
                                        value={form.start_date}
                                        onChange={(e) => setField("start_date", e.target.value)}
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="ab-end-d">
                                        End date <span className="admission-file-upload__hint">(optional)</span>
                                    </label>
                                    <input
                                        id="ab-end-d"
                                        type="date"
                                        className="admission-form-input"
                                        value={form.end_date}
                                        onChange={(e) => setField("end_date", e.target.value)}
                                    />
                                    <p className="admission-file-upload__hint">Past end dates auto-mark batch inactive on load.</p>
                                </div>
                            </div>
                        </section>

                        <section className="admission-form-section" aria-labelledby="ab-cap">
                            <h2 id="ab-cap" className="admission-form-section__title">
                                Capacity &amp; control
                            </h2>
                            <div className="admission-form-grid">
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="ab-max">
                                        Max students <span className="admission-file-upload__hint">(optional)</span>
                                    </label>
                                    <input
                                        id="ab-max"
                                        type="number"
                                        min={0}
                                        className="admission-form-input"
                                        value={form.max_students}
                                        onChange={(e) => setField("max_students", e.target.value)}
                                        placeholder="Unlimited if empty"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="ab-status">
                                        Status
                                    </label>
                                    <select
                                        id="ab-status"
                                        className="admission-form-select"
                                        value={form.status}
                                        onChange={(e) => setField("status", e.target.value)}
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </section>

                        <section className="admission-form-section" aria-labelledby="ab-extra">
                            <h2 id="ab-extra" className="admission-form-section__title">
                                Optional
                            </h2>
                            <div className="admission-form-grid">
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="ab-type">
                                        Batch type
                                    </label>
                                    <select
                                        id="ab-type"
                                        className="admission-form-select"
                                        value={form.batch_type}
                                        onChange={(e) => setField("batch_type", e.target.value)}
                                    >
                                        <option value="">—</option>
                                        <option value="Regular">Regular</option>
                                        <option value="Weekend">Weekend</option>
                                        <option value="Crash Course">Crash Course</option>
                                    </select>
                                </div>
                                <div className="admission-form-field admission-form-field--full">
                                    <label className="admission-form-field__label" htmlFor="ab-notes">
                                        Internal notes
                                    </label>
                                    <textarea
                                        id="ab-notes"
                                        className="admission-form-input"
                                        rows={3}
                                        value={form.notes}
                                        onChange={(e) => setField("notes", e.target.value)}
                                        placeholder="Internal comments"
                                    />
                                </div>
                            </div>
                        </section>

                        <footer className="admission-form-footer">
                            <button
                                type="button"
                                className="admission-form-btn admission-form-btn--secondary"
                                onClick={() => navigate("/admission-batches")}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="admission-form-btn admission-form-btn--primary" disabled={saving}>
                                {saving ? "Saving…" : isEdit ? "Save batch" : "Create batch"}
                            </button>
                        </footer>
                    </form>
                </div>
            </div>
        </div>
    );
}
